import { Button } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import {
  showChurnSurveyModal,
  useExperimentChurnDeflectionModalV1,
  showChurnDeflectionModal as showChurnDeflectionModalV1,
} from '@hotjar/voluntary-churn';
import type {
  Context,
  Event,
} from '~/subscription-management/checkout/state-machine/checkout.machine.types';
import { showChurnDeflectionModal } from '~/subscription-management/features/churn/churn-deflection-modal/ChurnDeflectionModal';
import { prettifyDate } from '~/subscription-management/utils/date.utils';

interface Props {
  isLoading: boolean;
  isDisabled: boolean;
  context: Context;
  send: (event: Event) => void;
}

export const ChurnFooter = ({ isLoading, isDisabled, context, send }: Props): JSX.Element => {
  const { t } = useTranslationWithPrefix('checkout:churn');
  const {
    allowancesToRemove,
    accountId,
    subscription,
    billingPeriod,
    customer: { currentPeriodCostSummary, billingAddress, currency },
  } = context;

  const isChurnDeflectionModalV1ExperimentEnabled = useExperimentChurnDeflectionModalV1(
    allowancesToRemove,
    {
      billingPeriod,
      currency,
    },
  );

  const nextChargeDate = prettifyDate(subscription?.currentPeriodEndDate, billingAddress?.country);

  const handleClick = async () => {
    try {
      if (isChurnDeflectionModalV1ExperimentEnabled) {
        await showChurnDeflectionModalV1({
          subscriptionEndDate: nextChargeDate,
          currentProducts: allowancesToRemove,
          billingPeriod,
          currency,
        });
      } else {
        await showChurnDeflectionModal({
          subscriptionEndDate: nextChargeDate,
          currentProducts: allowancesToRemove,
        });
      }
      await showChurnSurveyModal({
        subscriptionSubtotal: currentPeriodCostSummary?.amountWithoutTax.toString() ?? '',
        accountId: accountId.toString(),
        allowancesToRemove,
      });
      send({ type: 'CHURN' });
    } catch {
      // Leave churn flow
      send({ type: 'SETUP_SUBSCRIPTION', resetAllowances: true });
    }
  };

  return (
    <>
      <Button kind="danger" onClick={handleClick} isDisabled={isDisabled} isLoading={isLoading}>
        {t('cancel-subscription')}
      </Button>
      <Button
        kind="ghost"
        onClick={() => {
          send({ type: 'SETUP_SUBSCRIPTION', resetAllowances: true });
        }}
        isLoading={isLoading}
        isDisabled={isDisabled}
      >
        {t('keep-plans')}
      </Button>
    </>
  );
};
